import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { X, ArrowRight, Barbell, Brain, ChartBar, Trophy, Crown, Lightning } from '@phosphor-icons/react';

const LS_KEY = 'gritfit_tour_completed_v1';

const SLIDES = [
  {
    icon: Lightning,
    color: '#FF3B30',
    title: "Welcome to GRIT.FIT",
    body: "No equipment. No excuses. 21 bodyweight exercises, AI-generated plans, and a workout player that keeps you moving even when your hands are sweaty.",
    cta: null,
  },
  {
    icon: Barbell,
    color: '#FF3B30',
    title: "Your Workout Arsenal",
    body: "Browse the library, build custom routines, or let our AI craft a personalized plan. Every exercise has form tips, easier modifications, and a video demo.",
    cta: { label: "See workouts", path: "/workouts" },
  },
  {
    icon: ChartBar,
    color: '#007AFF',
    title: "Track Everything",
    body: "Analytics shows your 12-week volume, personal records, and a 90-day activity heatmap. Hit the Leaderboards tab to see how you rank — or stay private, it's up to you.",
    cta: { label: "View Analytics", path: "/analytics" },
  },
  {
    icon: Trophy,
    color: '#FF9500',
    title: "Stay Consistent",
    body: "Enable daily push reminders, pair with a buddy, or go Pro for Smart Mode (learns your workout time) + Streak-Save alerts that save you from breaking your streak.",
    cta: { label: "Go Pro", path: "/pricing", icon: Crown },
  },
];

export default function OnboardingTour() {
  const [visible, setVisible] = useState(false);
  const [slideIdx, setSlideIdx] = useState(0);

  useEffect(() => {
    try {
      const done = window.localStorage.getItem(LS_KEY);
      if (!done) {
        // Delay slightly so dashboard paints first
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch { /* ignore */ }
  }, []);

  const complete = () => {
    try { window.localStorage.setItem(LS_KEY, new Date().toISOString()); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  const slide = SLIDES[slideIdx];
  const Icon = slide.icon;
  const isLast = slideIdx === SLIDES.length - 1;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm p-0 sm:p-4"
      data-testid="onboarding-tour"
      role="dialog"
      aria-labelledby="onboarding-title"
    >
      <div className="w-full max-w-md bg-[#0A0A0A] border border-white/10 relative">
        {/* Close */}
        <button
          onClick={complete}
          className="absolute top-4 right-4 text-[#71717A] hover:text-white transition-colors z-10"
          data-testid="onboarding-close"
          aria-label="Skip tour"
        >
          <X weight="bold" className="w-5 h-5" />
        </button>

        {/* Progress dots */}
        <div className="flex gap-1.5 px-6 pt-6">
          {SLIDES.map((_, i) => (
            <div
              key={i}
              className={`h-1 flex-1 transition-all duration-300 ${i < slideIdx ? 'bg-[#FF3B30]/60' : i === slideIdx ? 'bg-[#FF3B30]' : 'bg-white/10'}`}
            />
          ))}
        </div>

        {/* Icon hero */}
        <div className="px-6 pt-6 pb-2">
          <div
            className="w-14 h-14 flex items-center justify-center"
            style={{ backgroundColor: `${slide.color}18`, border: `1px solid ${slide.color}44` }}
          >
            <Icon weight="fill" className="w-7 h-7" style={{ color: slide.color }} />
          </div>
        </div>

        {/* Copy */}
        <div className="px-6 pb-6">
          <h2 id="onboarding-title" className="text-3xl sm:text-4xl font-black tracking-tighter mb-3" data-testid="onboarding-title">
            {slide.title}
          </h2>
          <p className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed">
            {slide.body}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row gap-2 px-6 pb-6">
          {slide.cta && (
            <Link
              to={slide.cta.path}
              onClick={complete}
              className="btn-secondary px-5 py-3 text-sm flex items-center justify-center gap-2"
              data-testid={`onboarding-cta-${slideIdx}`}
            >
              {slide.cta.icon && <slide.cta.icon weight="fill" className="w-4 h-4" />}
              {slide.cta.label}
            </Link>
          )}
          <button
            onClick={() => (isLast ? complete() : setSlideIdx(i => i + 1))}
            className="btn-primary px-6 py-3 text-sm flex-1 flex items-center justify-center gap-2"
            data-testid={`onboarding-next-${slideIdx}`}
          >
            {isLast ? "Start training" : "Next"}
            <ArrowRight weight="bold" className="w-4 h-4" />
          </button>
        </div>

        {/* Skip link (subtle) */}
        {!isLast && (
          <button
            onClick={complete}
            className="w-full text-center py-3 text-xs text-[#52525B] hover:text-[#A1A1AA] border-t border-white/5 transition-colors"
            data-testid="onboarding-skip"
          >
            Skip tour
          </button>
        )}
      </div>
    </div>
  );
}
